import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { UserContext } from '@/contexts/UserContext';
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ApolloClient, ApolloLink, ApolloProvider, HttpLink, split, useLazyQuery, useMutation } from '@apollo/client';
import cache from '@/graphql/cache';
import { serializingLink } from '@/graphql/serializingLink';
import resolvers from '@/graphql/resolvers';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { useQuery } from '@apollo/client';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { createTheme, ThemeProvider } from '@material-ui/core/styles';
import api from '@/utilities/api';
import { getImpersonatedClientId, clearImpersonation } from '@/utilities/impersonation';
import { useOrgSimulation } from '@/hooks/useOrgSimulation';
import { useNavigate } from 'react-router-dom';
// Generated by scripts/build-bootstrap-css.js — upstream bootstrap.css with its
// ~1,364 !important declarations removed. Same values, but Bootstrap no longer
// outranks every other stylesheet in the app. See docs/tailwind-spike-findings.md.
import '../styles/bootstrap.deescalated.css';
import ReactGA from 'react-ga4';
import * as ROUTES from '../constants/routes';
import '../styles/App.css';
import SigninComponent from './Auth/Auth';
import PendingAccess from './Auth/PendingAccess';
import { hasHasuraAccess } from './Auth/accessClaims';
import { createAccessErrorLink } from './Auth/accessErrorLink';
import AuthContactPage from './AuthContact';
import MyOrders from './MyOrders';
import AuthResetPasswordRequest from './AuthResetPasswordRequest';
import AuthResetPassword from './AuthResetPassword';
import AdminCommunications from './admin/AdminCommunications';
import AdminFTLBoard from './admin/AdminFTLBoard';
import AdminEditFTLRoute from './admin/AdminEditFTLRoute';
import AdminTariffs from './admin/AdminTariffs';
import AdminExceptions from './admin/AdminExceptions';
import AdminPricing from './admin/AdminPricing';
import AdminNotes from './admin/AdminNotes';
import AdminIntegrations from './admin/AdminIntegrations';
import AdminAutomations from './admin/AdminAutomations';
import {
    ADMIN_CLIENTS_BY_ORGANIZATION,
    CLIENT_BY_ID,
    QUERY_BY_FIREBASE_ID,
    TEAMMATE_CLIENT_BY_ID,
} from '../graphql/queries/users';
import { RESOURCE_SCHEDULE_CUTOFF } from '@/constants/resourceScheduling';
import { GET_ORG_TAGS } from '../graphql/queries/tags';
import { UPDATE_GEOLOCATION } from '@/graphql/mutations/users';
import AdminUsers from './admin/AdminUsers';
import Navigation from './Navigation';
import NavResponsiveContainer from './Navigation/NavResponsiveContainer';
import { useMediaQuery } from 'react-responsive';
import { CONFIG } from '@/config';
import Account from './Account';
import Tariff from './Account/Tariffs';
import ShipmentForm from './ShipmentForm';
import JobDetails from './ShipmentForm/JobDetails';
import Loader from './Loader';
import DispatchPlan from './DispatchPlan';
import DispatchUnassigned from './DispatchUnassigned';
import Tracking from './Tracking';
import AdminShipperDetails from './admin/AdminShipperDetails';
import AdminCarrierDetails from './admin/AdminCarrierDetails';
import CustomerPredelivery from './CustomerPredelivery';
import CustomerTracking from './CustomerTrack';
import CustomerRating from './CustomerRating';
import ExceptionManager from './ExceptionManager';

import ShipperSignUp from './ShipperSignUp';
import CarrierSignUp from './CarrierSignUp';
import Subregions from './Subregions';
import TeammateSignUp from './TeammateSignUp';

import CarrierFTLBoard from './CarrierFTLBoard';
import MyRoutes from './MyRoutes';
import OrderDetailsPage from './OrderDetailsPage';
import ListingDetailsPage from './ListingDetailsPage';
import * as Sentry from '@sentry/react';
import { ErrorFallback } from './ErrorBoundary';
import SaaSRouteDetails from './RouteDetails';
import LogRocket from 'logrocket';
import AdminAccountReset from './admin/AdminAccountReset';
import LoadBoardLTL from './LoadBoardLTL';
import InstantQuote from './InstantQuote';
import AdminOrders from './admin/AdminOrders';
import Resources from './Resources';
import ResourceNotifications from './ResourceNotifications';
import PrivacyPage from './Privacy';
import ShipperIntegrations from './Integrations';
import CancelPage from './Cancel';
import AdminMatchingTool from './admin/AdminMatchingTool';
import AuctionedOrders from './AuctionedOrders';
import CustomerConfirmTimeframe from './CustomerConfirmTimeframe';
import CorporateSignup from './CorporateSignup';
import CorporateUsers from './CorporateUsers';
import CorporateShipper from './CorporateShipper';
import CorporateShipperTariff from './CorporateShipper/ShipperTariff';
import CorporateTags from './CorporateTags';
import OrderDetailsPageDriver from './OrderDetailsPageDriver';
import TrackOrder from './TrackOrder';
import Manifests from './Manifests';
import Manifest from './Manifest';
import CycleCounts from './CycleCounts';
import CycleCount from './CycleCount';
import CycleCountLocation from './CycleCountLocation';
import CycleCountItem from './CycleCountItem';
import WarehouseSetup from './WarehouseSetup';
import InventoryManagement from './InventoryManagement';
import DriverPayments from './DriverPayments';
import DriverPayment from './DriverPayment';
import Documentation from './Documentation';
import SsoCallback from './Auth/SsoCallback';
import SsoRedirect from './Auth/SsoRedirect';
import CarrierOptions from './Subregions/CarrierOptions';
import AutoAssign from './Subregions/AutoAssign';
import DeliveryProfilesGrid from './Subregions/AutoAssign/ProfilesGrid';
import Reports from './Reports';
import AdminTags from './admin/AdminTags';
import Claims from './Claims';
import ClaimDetails from './Claims/ClaimDetails';
import AccountingAccessorials from './Accessorials';
import AccountingOrders from './Accounting/CompletedOrders';
import AccountingInvoice from './Accounting/Invoice';
import AccountingInvoices from './Accounting/Invoices';
import AccountingReceivables from './admin/AdminFinancials/receivables';
import AccountingPayables from './admin/AdminFinancials/payables';
import CustomerRoot from './Customer';

export { UserContext };

const theme = createTheme({
    typography: {
        fontFamily: ['Montserrat', 'Roboto', 'Arial', 'sans-serif'],
        button: {
            fontWeight: '700',
            textTransform: 'capitalize',
        },
    },
    palette: {
        primary: {
            main: '#59b863',
        },
    },
});

const UserWrapper = ({ firebaseUser, children, isAuthenticated }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const [customHeader, setCustomHeader] = useState(null);
    const [customColor, setCustomColor] = useState(null);

    const isSmall = useMediaQuery({ query: '(max-width: 1280px)' });
    const [sidebarCollapsed, setSidebarCollapsed] = useState(isSmall);

    useEffect(() => {
        setSidebarCollapsed(isSmall);
    }, [isSmall]);

    const { loading, data } = useQuery(QUERY_BY_FIREBASE_ID, {
        variables: { legacy_user_id: firebaseUser?.uid },
        skip: !firebaseUser || firebaseUser?.uid === 'anonymous-user',
        onError: (error) => {
            console.error(error);
            Sentry.captureException(error);
        },
        onCompleted: (result) => {
            const hasuraUser = result?.users?.[0];
            if (hasuraUser) {
                LogRocket.identify(firebaseUser?.uid, {
                    email: hasuraUser?.email,
                    name: hasuraUser?.username,
                    user_id: hasuraUser?.user_id,
                });
                Sentry.setUser({
                    id: hasuraUser.user_id,
                    username: hasuraUser.username,
                    email: hasuraUser.email,
                });
            }
        },
    });

    const [updateGeolocation] = useMutation(UPDATE_GEOLOCATION, {
        onError: (e) => {
            console.error(e);
            Sentry.captureException(e);
        },
    });

    const [clientId, setClientId] = useState(getImpersonatedClientId());

    // Re-sync impersonation from storage whenever the signed-in identity changes.
    // The auth observer / sign-in handlers clear localStorage on sign-in and
    // sign-out, so this drops any stale client_id when the user changes while a
    // normal refresh (same uid) restores the admin's active impersonation.
    useEffect(() => {
        setClientId(getImpersonatedClientId());
    }, [firebaseUser?.uid]);

    // The signed-in users row. `user` is this row, or an org admin overlay of it during an org sim.
    const realUser = data?.users?.[0];
    const { user, simOrgId, setSimOrgId, loading: simOrgLoading } = useOrgSimulation(realUser, firebaseUser?.uid);
    const [getClient, { loading: clientLoading, data: clientData }] = useLazyQuery(CLIENT_BY_ID);
    const [getTeammateClient, { loading: teammateLoading, data: teammateData }] = useLazyQuery(TEAMMATE_CLIENT_BY_ID);
    const [getAdminClientsByOrganization, { loading: orgLoading, data: orgData, refetch: refetchAdminClientsByOrg }] =
        useLazyQuery(ADMIN_CLIENTS_BY_ORGANIZATION);
    const [getOrgTags, { data: orgTagsData }] = useLazyQuery(GET_ORG_TAGS, {
        onError: (e) => {
            console.error(e);
            Sentry.captureException(e);
        },
    });

    const refetchAdminClients = useCallback(async () => {
        if (refetchAdminClientsByOrg) return refetchAdminClientsByOrg();
        return null;
    }, [refetchAdminClientsByOrg]);

    const [client, teammate] = useMemo(() => {
        let client, teammate;

        if (teammateData?.teammate) {
            const { userByTeamLeaderId, ...rest } = teammateData.teammate;
            client = userByTeamLeaderId?.client;
            teammate = rest;
        } else {
            client = clientData?.client;
            teammate = null;
        }

        let useClient = true;
        if ((user?.roles?.['ADMIN'] || user?.roles?.['ONWARD_ADMIN']) && !clientId) useClient = false;
        const useTeammate = user?.roles?.['TEAMMATE'];

        return [useClient ? client : null, useTeammate ? teammate : null];
    }, [user, clientId, clientData, teammateData]);

    const admin_clients = useMemo(() => {
        if (user?.roles?.['ADMIN']) {
            return orgData?.clients || [];
        }
        return null;
    }, [user, orgData]);

    // The lazy query keeps its last result, so an admin who leaves a simulated org would otherwise keep its tags.
    const org_tags = useMemo(
        () => (user?.organization_id ? orgTagsData?.tags || [] : []),
        [user?.organization_id, orgTagsData]
    );

    useEffect(() => {
        if (
            !isAuthenticated &&
            ![
                /^\/customer/,
                /^\/documentation/,
                /^\/rate/,
                /^\/signin/,
                /^\/(shipper|carrier|teammate|corporate)\/signup/,
                /^\/pw-forget/,
                // Not covered by the /auth/:type/ rule below: it has no third segment to match on.
                /^\/auth\/password-reset/,
                /^\/auth\/[^/]+\//,
                /^\/instant-quote/,
                /^\/privacy/,
                /^\/cancel/,
                /^\/order\/([0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12})\/driver/,
            ].some((unAuthPath) => unAuthPath.test(location.pathname))
        ) {
            console.log('redirect');
            navigate('/signin');
        }
    }, [isAuthenticated]);

    // Super user client login
    useEffect(() => {
        if ((user?.roles?.['ADMIN'] || user?.roles?.['ONWARD_ADMIN']) && clientId) {
            getClient({ variables: { client_id: clientId, cutoff: RESOURCE_SCHEDULE_CUTOFF } });
        }
    }, [user, clientId]);

    // Regular user client/teammate/admin login
    useEffect(() => {
        // CONTRACTOR keeps a clients row like CARRIER does. Without it `client` stays undefined and
        // every downstream branch reading client?.user?.roles silently never fires.
        if (user?.roles?.['SHIPPER'] || user?.roles?.['CARRIER'] || user?.roles?.['CONTRACTOR']) {
            getClient({ variables: { client_id: user.user_id, cutoff: RESOURCE_SCHEDULE_CUTOFF } });
        } else if (user?.roles?.['TEAMMATE']) {
            getTeammateClient({ variables: { teammate_id: user.user_id, cutoff: RESOURCE_SCHEDULE_CUTOFF } });
        } else if (user?.roles?.['ADMIN'] && user?.organization_id) {
            getAdminClientsByOrganization({
                variables: { organization_id: user.organization_id, cutoff: RESOURCE_SCHEDULE_CUTOFF },
            });
        }
    }, [user]);

    // Load org-level tags for anyone belonging to an organization for use in useClientUser
    useEffect(() => {
        if (user?.organization_id) {
            getOrgTags({ variables: { organization_id: user.organization_id } });
        }
    }, [user]);

    // Update geolocation on users table at sign-in
    useEffect(() => {
        (async () => {
            if (isAuthenticated && data?.users?.[0]) {
                try {
                    let currentDate = Date.now();
                    let expirationDate = data?.users?.[0]?.geolocation_expiration || new Date(0);
                    if (currentDate > expirationDate) {
                        // Expires after 10 days
                        const oneDay = 24 * 60 * 60 * 1000;
                        const newExpirationDate = new Date(currentDate + oneDay * 10);

                        const pos = await new Promise((resolve, reject) => {
                            navigator.geolocation.getCurrentPosition(resolve, reject);
                        });

                        updateGeolocation({
                            variables: {
                                user_id: data?.users?.[0]?.user_id,
                                geolocation: {
                                    lat: pos.coords.latitude,
                                    lng: pos.coords.longitude,
                                },
                                geolocation_expiration: newExpirationDate.toISOString(),
                            },
                        });
                    }
                } catch (e) {
                    // User has denied geolocation access.
                    console.error(e);
                }
            }
        })();
    }, [isAuthenticated, data]);

    const loadingClient = clientLoading || (clientId && !client);
    if (loading || loadingClient || teammateLoading || orgLoading || simOrgLoading) {
        return <Loader />;
    }

    return (
        <UserContext.Provider
            value={{
                customHeader,
                setCustomHeader,
                customColor,
                setCustomColor,
                sidebarCollapsed,
                setSidebarCollapsed,
                clientId,
                setClientId,
                simOrgId,
                setSimOrgId,
                firebaseUser,
                user,
                realUser,
                organization: user?.organization || null,
                client,
                teammate,
                admin_clients,
                org_tags,
                refetchAdminClients,
            }}
        >
            {children}
        </UserContext.Provider>
    );
};

// How often the pending-approval splash re-reads the token claims to see whether access was granted.
const ACCESS_CHECK_INTERVAL_MS = 30000;

const AuthWrapper = ({ children, history }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    // const [accessToken, setAccessToken] = useState(null);
    const [firebaseUser, setFirebaseUser] = useState(null);
    // Whether the Firebase token claims carry Hasura access. Without it, the app is swapped for the
    // PendingAccess splash (only when a Firebase user is signed in).
    const [hasAccess, setHasAccess] = useState(true);
    // True while a token refresh for an access check is running, so checks never overlap.
    const accessCheckInFlightRef = useRef(false);
    // Tracks the last observed uid so we can distinguish a genuine sign-out /
    // user switch (clear impersonation) from a normal refresh that re-fires the
    // observer with the same uid (keep impersonation). `undefined` = cold load.
    const prevUidRef = useRef(undefined);

    useEffect(() => {
        // E2E auth bypass — Playwright injects window.__E2E_AUTH_USER__ via addInitScript.
        // Only available in non-production environments.
        if (window.__E2E_AUTH_USER__ && process.env.NODE_ENV !== 'production') {
            const fakeUser = window.__E2E_AUTH_USER__;
            fakeUser.getIdToken = () => Promise.resolve('e2e-fake-token');
            setIsAuthenticated(true);
            setHasAccess(true);
            setIsLoading(false);
            setFirebaseUser(fakeUser);
            return;
        }

        const unregisterAuthObserver = onAuthStateChanged(getAuth(), async (user) => {
            console.debug('Auth State Changed');
            if (user) {
                // User is signed in.
                console.debug('User is signed in');
                if (process.env.REACT_APP_ENVIRONMENT === 'development') console.debug(user);
                const hasObservedAuthState = prevUidRef.current !== undefined;
                const authIdentityChanged = prevUidRef.current !== user.uid;
                if (hasObservedAuthState && authIdentityChanged) {
                    clearImpersonation();
                }
                prevUidRef.current = user.uid;
                if (user.uid !== 'anonymous-user') {
                    LogRocket.identify(user.uid);
                    Sentry.setUser({ id: user.uid });
                }
                let nextHasAccess;
                try {
                    const { claims } = await user.getIdTokenResult();
                    nextHasAccess = hasHasuraAccess(claims);
                } catch (error) {
                    // Can't read the claims: show the splash, whose check-again retries recover from it.
                    console.error('Could not read token claims', error);
                    nextHasAccess = false;
                }
                // A sign-out or user switch during the await fires its own callback; don't let this
                // stale one overwrite it.
                if (getAuth().currentUser?.uid !== user.uid) return;
                setHasAccess(nextHasAccess);
                setIsAuthenticated(true);
                setIsLoading(false);
                setFirebaseUser(user);
                api.setToken(user);
                window?.localStorage?.setItem('onward.already-signed-in', 1);
            } else {
                // User is signed out.
                console.debug('User is signed out');
                // Only a real sign-out (we had a user) clears impersonation; the
                // initial cold-load null callback must leave storage untouched.
                if (prevUidRef.current) {
                    clearImpersonation();
                }
                prevUidRef.current = null;
                setIsAuthenticated(false);
                setIsLoading(false);
                setFirebaseUser(null);
                window?.localStorage?.removeItem('onward.already-signed-in');
            }
        });

        return () => {
            unregisterAuthObserver();
        };
    }, []);

    // Forces a token refresh, re-reads the claims, and hands them to `onClaims`. One run at a time.
    const refreshAccess = useCallback(
        async (onClaims) => {
            if (!firebaseUser || accessCheckInFlightRef.current) return;
            accessCheckInFlightRef.current = true;
            try {
                await firebaseUser.getIdToken(true);
                const { claims } = await firebaseUser.getIdTokenResult();
                // The user signed out or switched while the refresh ran: these claims are stale.
                if (getAuth().currentUser?.uid !== firebaseUser.uid) return;
                onClaims(claims);
            } catch (error) {
                console.error('Access check failed', error);
            } finally {
                accessCheckInFlightRef.current = false;
            }
        },
        [firebaseUser]
    );

    // The splash's check: load the app fresh once Hasura access appears. It goes to `/` rather than
    // reloading, so a user parked on the SSO callback URL doesn't replay a used code.
    const checkAgain = useCallback(
        () =>
            refreshAccess((claims) => {
                if (hasHasuraAccess(claims)) window.location.assign('/');
            }),
        [refreshAccess]
    );

    // An access error on a live session only ever means access was lost: confirm against a fresh
    // token and show the splash. If access is still there the error was something else; do nothing.
    const handleAccessError = useCallback(
        () =>
            refreshAccess((claims) => {
                if (!hasHasuraAccess(claims)) setHasAccess(false);
            }),
        [refreshAccess]
    );

    const gated = !!firebaseUser && !hasAccess;

    useEffect(() => {
        if (!gated) return undefined;
        const interval = setInterval(checkAgain, ACCESS_CHECK_INTERVAL_MS);
        window.addEventListener('focus', checkAgain);
        return () => {
            clearInterval(interval);
            window.removeEventListener('focus', checkAgain);
        };
    }, [gated, checkAgain]);

    const wsLink = new GraphQLWsLink(
        createClient({
            url: CONFIG.WS_HASURA_URL,
            connectionParams: async () => {
                let auth = { 'X-Hasura-Admin-Secret': 'myadminsecretkey' };
                if (CONFIG.ENV !== 'development') {
                    const accessToken = await firebaseUser.getIdToken();
                    auth = { authorization: `Bearer ${accessToken}` };
                }

                return {
                    headers: {
                        ...auth,
                    },
                };
            },
        })
    );
    const httpLink = new HttpLink({
        uri: CONFIG.HASURA_URL,
        fetchOptions: {
            method: 'POST',
        },
    });

    // The split function takes three parameters:
    //
    // * A function that's called for each operation to execute
    // * The Link to use for an operation if the function returns a "truthy" value
    // * The Link to use for an operation if the function returns a "falsy" value
    const splitLink = split(
        ({ query }) => {
            const definition = getMainDefinition(query);
            return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
        },
        wsLink,
        httpLink
    );

    const authMiddleware = new ApolloLink(async (operation, forward) => {
        const definition = getMainDefinition(operation.query);
        if (definition.kind === 'OperationDefinition' && definition.operation !== 'subscription') {
            let auth = { 'X-Hasura-Admin-Secret': 'myadminsecretkey' };
            if (CONFIG.ENV !== 'development') {
                const accessToken = await firebaseUser.getIdToken();
                auth = { authorization: `Bearer ${accessToken}` };
            }

            operation.setContext(({ headers = {} }) => ({
                headers: {
                    ...headers,
                    ...auth,
                },
            }));
        }

        return forward(operation);
    });

    const accessErrorLink = createAccessErrorLink(handleAccessError);

    const client = new ApolloClient({
        // accessErrorLink must come after authMiddleware: that link is async, so its forward() is a Promise,
        // which onError can't subscribe to. Behind it, forward() is the split link's real Observable.
        // serializingLink first: operations with a serializationKey wait in
        // queue before auth runs, so tokens are fetched at send time.
        link: ApolloLink.from([serializingLink, authMiddleware, accessErrorLink, splitLink]),
        cache,
        connectToDevTools: CONFIG.ENV !== 'production',
        resolvers,
    });

    if (isLoading) {
        return <Loader />;
    }

    if (gated) {
        return (
            <ThemeProvider theme={theme}>
                <PendingAccess onCheckAgain={checkAgain} onSignOut={() => signOut(getAuth())} />
            </ThemeProvider>
        );
    }

    return (
        <ApolloProvider client={client}>
            <UserWrapper firebaseUser={firebaseUser} children={children} isAuthenticated={isAuthenticated} />
        </ApolloProvider>
    );
};

// prettier-ignore
const App = () => {
    useEffect(() => {
        ReactGA.send({ hitType: 'pageview', page: window.location.pathname + window.location.search });
    }, []);

    return (
        <Router>
            <AuthWrapper>
                <ThemeProvider theme={theme}>
                    <Navigation />
                    <NavResponsiveContainer>
                        <Sentry.ErrorBoundary fallback={ErrorFallback}>
                            <Routes>
                                {/* Account & SignIn */}
                                <Route exact path={ROUTES.SIGNIN} element={<SigninComponent />} />
                                <Route exact path={ROUTES.SSO_CALLBACK} element={<SsoCallback />} />
                                <Route exact path={ROUTES.SSO_REDIRECT} element={<SsoRedirect />} />
                                <Route exact path={ROUTES.ACCOUNT} element={<Account />} />
                                <Route exact path={ROUTES.ACCOUNT_TARIFFS} element={<Tariff />} />
                                <Route exact path={ROUTES.SUBREGION} element={<Subregions />} />
                                <Route exact path={ROUTES.AUTO_ASSIGN} element={<DeliveryProfilesGrid />} />
                                <Route exact path={ROUTES.AUTO_ASSIGN_PROFILE} element={<AutoAssign />} />
                                <Route exact path={ROUTES.RATE_SHOP} element={<CarrierOptions />} />
                                <Route exact path={ROUTES.RESOURCES} element={<Resources />} />
                                <Route exact path={ROUTES.NOTIFICATIONS} element={<ResourceNotifications />} />
                                <Route exact path={ROUTES.SHIPPER_SIGN_UP} element={<ShipperSignUp />} />
                                <Route exact path={ROUTES.CARRIER_SIGN_UP} element={<CarrierSignUp />} />
                                <Route exact path={ROUTES.TEAMMATE_SIGN_UP} element={<TeammateSignUp />} />
                                <Route exact path={ROUTES.CORPORATE_SIGNUP} element={<CorporateSignup />} />
                                <Route exact path={ROUTES.PASSWORD_FORGET} element={<AuthResetPasswordRequest />} />
                                <Route exact path={ROUTES.PASSWORD_RESET_PAGE} element={<AuthResetPassword />} />

                                {/* Admin Routes */}
                                <Route exact path={ROUTES.ADMIN_USERS} element={<AdminUsers />} />
                                <Route exact path={ROUTES.ADMIN_SHIPPER} element={<AdminShipperDetails />} />
                                <Route exact path={ROUTES.ADMIN_CARRIER} element={<AdminCarrierDetails />} />
                                <Route exact path={ROUTES.ADMIN_ORDERS} element={<AdminOrders />} />
                                <Route exact path={ROUTES.ADMIN_COMMUNICATIONS} element={<AdminCommunications />} />
                                <Route exact path={ROUTES.ADMIN_FTL} element={<AdminFTLBoard />} />
                                <Route exact path={ROUTES.ADMIN_TRACKING_FTL} element={<Tracking />} />
                                <Route exact path={ROUTES.ADMIN_TRACKING_LTL} element={<Tracking />} />
                                <Route exact path={ROUTES.ADMIN_EDITFTLROUTE} element={<AdminEditFTLRoute />} />
                                <Route exact path={ROUTES.ADMIN_ACCOUNT_RESET} element={<AdminAccountReset />} />
                                <Route exact path={ROUTES.ADMIN_MATCHING_TOOL} element={<AdminMatchingTool />} />
                                <Route exact path={ROUTES.ADMIN_TARIFFS} element={<AdminTariffs />} />
                                <Route exact path={ROUTES.ADMIN_TAGS} element={<AdminTags />} />
                                <Route exact path={ROUTES.ADMIN_EXCEPTIONS} element={<AdminExceptions />} />
                                <Route exact path={ROUTES.ADMIN_PRICING} element={<AdminPricing />} />
                                <Route exact path={ROUTES.ADMIN_NOTES} element={<AdminNotes />} />
                                <Route exact path={ROUTES.ADMIN_INTEGRATIONS} element={<AdminIntegrations />} />
                                <Route exact path={ROUTES.ADMIN_AUTOMATIONS} element={<AdminAutomations />} />
                                <Route exact path={ROUTES.ADMIN_REPORTS} element={<Reports />} />

                                {/* Accounting Routes */}
                                <Route exact path={ROUTES.ACCOUNTING_ACCESSORIALS} element={<AccountingAccessorials />} />
                                <Route exact path={ROUTES.ACCOUNTING_ORDERS} element={<AccountingOrders />} />
                                <Route exact path={ROUTES.ACCOUNTING_INVOICE} element={<AccountingInvoice />} />
                                <Route exact path={ROUTES.ACCOUNTING_INVOICES} element={<AccountingInvoices />} />
                                <Route exact path={ROUTES.ACCOUNTING_RECEIVABLES} element={<AccountingReceivables />} />
                                <Route exact path={ROUTES.ACCOUNTING_PAYABLES} element={<AccountingPayables />} />

                                {/* User Routes */}
                                <Route exact path={ROUTES.CLAIMS} element={<Claims />} />
                                <Route exact path={ROUTES.CLAIM_DETAILS} element={<ClaimDetails />} />
                                <Route exact path={ROUTES.WAREHOUSES} element={<WarehouseSetup />} />
                                <Route exact path={ROUTES.MANIFESTS} element={<Manifests />} />
                                <Route exact path={ROUTES.INVENTORY} element={<InventoryManagement />} />
                                <Route exact path={ROUTES.MANIFEST} element={<Manifest />} />
                                <Route exact path={ROUTES.CYCLE_COUNTS} element={<CycleCounts />} />
                                <Route exact path={ROUTES.CYCLE_COUNT} element={<CycleCount />} />
                                <Route
                                    exact
                                    path={ROUTES.CYCLE_COUNT_LOCATION}
                                    element={<CycleCountLocation />}
                                />
                                <Route
                                    exact
                                    path={ROUTES.CYCLE_COUNT_ITEM}
                                    element={<CycleCountItem />}
                                />
                                <Route exact path={ROUTES.DISPATCH_PLAN} element={<DispatchPlan />} />
                                <Route exact path={ROUTES.CARRIER_DISPATCH_UNASSIGNED} element={<DispatchUnassigned />} />
                                <Route exact path={ROUTES.SHIPPER_DISPATCH_UNASSIGNED} element={<DispatchUnassigned />} />
                                <Route exact path={ROUTES.SHIPPER_PRICING} element={<ShipperIntegrations />} />
                                <Route exact path={ROUTES.CARRIER_LOADBOARD_LTL} element={<LoadBoardLTL />} />
                                <Route exact path={ROUTES.SHIPPER_TRACKING} element={<Tracking />} />
                                <Route exact path={ROUTES.CARRIER_TRACKING} element={<Tracking />} />
                                <Route exact path={ROUTES.CARRIER_LOADBOARD_FTL} element={<CarrierFTLBoard />} />
                                <Route exact path={ROUTES.SHIPPER_ROUTEDETAILS} element={<SaaSRouteDetails />} />
                                <Route exact path={ROUTES.ADMIN_ROUTEDETAILS} element={<SaaSRouteDetails />} />
                                <Route exact path={ROUTES.CARRIER_ROUTEDETAILS} element={<SaaSRouteDetails />} />
                                <Route exact path={ROUTES.CARRIER_OMS_MYROUTES} element={<MyRoutes />} />
                                <Route exact path={ROUTES.SHIPPER_OMS_MYROUTES} element={<MyRoutes />} />
                                <Route exact path={ROUTES.IMPORT_ORDERS} element={<ShipmentForm />} />
                                <Route exact path={ROUTES.JOB_DETAILS} element={<JobDetails />} />
                                <Route exact path={ROUTES.EXCEPTIONS} element={<ExceptionManager />} />
                                <Route path={ROUTES.ORDER_DETAILS_PAGE_DRIVER} element={<OrderDetailsPageDriver />} />
                                <Route path={ROUTES.ORDER_DETAILS_PAGE} element={<OrderDetailsPage />} />
                                <Route path={ROUTES.LISTING_DETAILS_PAGE} element={<ListingDetailsPage />} />
                                <Route exact path={ROUTES.SHIPPER_OMS_MYORDERS} element={<MyOrders />} />
                                <Route exact path={ROUTES.CARRIER_OMS_MYORDERS} element={<MyOrders />} />
                                <Route exact path={ROUTES.AUCTIONED_ORDERS_BIDS} element={<AuctionedOrders viewType="Quotes" />} />
                                <Route exact path={ROUTES.AUCTIONED_ORDERS_LISTINGS} element={<AuctionedOrders viewType="listings" />} />
                                <Route exact path={ROUTES.CORPORATE_USERS} element={<CorporateUsers />} />
                                <Route exact path={ROUTES.CORPORATE_SHIPPER} element={<CorporateShipper />} />
                                <Route
                                    exact
                                    path={ROUTES.CORPORATE_SHIPPER_TARIFF}
                                    element={<CorporateShipperTariff />}
                                />
                                <Route exact path={ROUTES.CORPORATE_TAGS} element={<CorporateTags />} />
                                <Route exact path={ROUTES.DRIVER_PAYMENTS} element={<DriverPayments />} />
                                <Route exact path={ROUTES.DRIVER_PAYMENT} element={<DriverPayment />} />

                                {/* Customer Routes */}
                                <Route exact path={ROUTES.CUSTOMER_CONFIRM_TIMEFRAME_PAGE} element={<CustomerConfirmTimeframe />} />
                                <Route exact path={ROUTES.CUSTOMER_PREDELIVERY_SURVEY_PAGE} element={<CustomerPredelivery />} />
                                <Route exact path={ROUTES.CUSTOMER_TRACK_ORDER_ID} element={<CustomerTracking />} />
                                <Route exact path={ROUTES.CUSTOMER_TRACK_TOKEN} element={<CustomerTracking />} />
                                <Route exact path={ROUTES.CUSTOMER_TRACK_BY_PO} element={<CustomerTracking />} />
                                <Route exact path={ROUTES.RATE} element={<CustomerRating />} />
                                <Route exact path={ROUTES.CUSTOMER} element={<CustomerRoot />} />

                                {/* Misc Routes */}
                                <Route exact path={ROUTES.AUTH_CONTACT} element={<AuthContactPage />} />
                                <Route exact path={ROUTES.INSTANT_QUOTE} element={<InstantQuote />} />
                                <Route exact path={ROUTES.CANCEL} element={<CancelPage />} />
                                <Route exact path={ROUTES.PRIVACY} element={<PrivacyPage />} />
                                <Route exact path={ROUTES.TRACK_ORDER} element={<TrackOrder />} />
                                <Route exact path={ROUTES.DOCUMENTATION} element={<Documentation />} />

                                {/* Catch all redirect to signin */}
                                <Route path="*" element={<Navigate replace to="/signin" />} />
                            </Routes>
                        </Sentry.ErrorBoundary>
                    </NavResponsiveContainer>
                </ThemeProvider>
            </AuthWrapper>
        </Router>
    );
};

export default App;
